import { Button, Flex, Rate, Space } from "antd";
import { Link } from "react-router";
import { useGetCoursesQuery } from "../../../app/features/admin/admin.api";
import type { CourseResponse } from "../../../app/features/admin/admin.types";
import EditIcon from "../../../assets/icons/EditIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import CustomTable from "../../../components/CustomTable";
import StatusTag from "../../../components/StatusTag";
import useFilters from "../../../hooks/useFilters";
import DashboardLayout from "../../../layouts/DashboardLayout";
import type { ColumnsType } from "../../../types/global.types";

const statusConfig = [
  { value: "DRAFT", label: "Draft", className: "bg-slate-100 text-slate-500" },
  {
    value: "PUBLISHED",
    label: "Published",
    className: "bg-green-100 text-green-600",
  },
  {
    value: "IN_REVIEW",
    label: "In review",
    className: "bg-blue-100 text-blue-600",
  },
  {
    value: "ARCHIVED",
    label: "Archived",
    className: "bg-orange-100 text-orange-600",
  },
  {
    value: "DELETED",
    label: "Deleted",
    className: "bg-red-100 text-red-600",
  },
];

export default function MyCourse() {
  const { limit, page, handleLimit, handlePagination } = useFilters();
  const { data: coursesData, isLoading } = useGetCoursesQuery();

  const columns: ColumnsType<CourseResponse> = [
    {
      title: "Course name",
      dataIndex: "title",
      key: "title",
      width: 300,
      render: (title, record) => (
        <Flex gap={12} align="center">
          <img
            src={
              record.thumbnailUrl ||
              "https://placehold.co/60x40/png?text=No+Image"
            }
            alt={title}
            className="w-16 h-12 rounded object-cover"
          />
          <div>
            <h4 className="font-medium">{title || "-"}</h4>
            <p className="text-theme-slate-700 text-xs font-normal mt-1">
              {record.Category?.name || "-"}
            </p>
          </div>
        </Flex>
      ),
    },
    {
      title: "Course Price",
      dataIndex: "price",
      key: "price",
      render: (price) => (
        <span>{price && Number(price) >= 0 ? `৳${Number(price).toLocaleString()}` : "-"}</span>
      ),
    },
    {
      title: "Special Price",
      dataIndex: "offerPrice",
      key: "offerPrice",
      render: (price) => (
        <span>
          {price && Number(price) >= 0
            ? `৳${Number(price).toLocaleString()}`
            : "-"}
        </span>
      ),
    },
    {
      title: "Rating",
      dataIndex: "ratingAvg",
      key: "ratingAvg",
      render: (rating) => (
        <Flex align="center" gap={4}>
          <Rate
            disabled
            defaultValue={1}
            count={1}
            className="text-[#FC8415] text-sm"
          />
          <span>{rating ? Number(rating).toFixed(1) : "0.0"}</span>
        </Flex>
      ),
    },
    {
      title: "Enrollments",
      dataIndex: "enrollmentCount",
      key: "enrollmentCount",
      render: (num) => (
        <span>
          {num !== undefined && num !== null ? num.toLocaleString() : "-"}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <StatusTag value={status || "-"} config={statusConfig} />
      ),
    },
    {
      title: "Action",
      dataIndex: "id",
      key: "action",
      align: "center",
      render: (id: number) => (
        <Space size={12}>
          <Link to={`/admin/my-course/${id}`}>
            <Button
              icon={<ViewIcon size={18} />}
              size="small"
              className="btn-icon"
            />
          </Link>
          <Link to={`/admin/my-course/manage-course/${id}`}>
            <Button
              icon={<EditIcon size={18} />}
              size="small"
              className="btn-icon"
            />
          </Link>
        </Space>
      ),
    },
  ];

  // const filterSection = (
  //   <Space>
  //     <Input
  //       prefix={<SearchIcon size={16} />}
  //       placeholder="Search course"
  //       className="w-64"
  //       allowClear
  //     />
  //     <Select
  //       placeholder="Category"
  //       style={{ width: 120 }}
  //       options={[{ label: "Design", value: "design" }]}
  //       allowClear
  //     />
  //     <Select
  //       placeholder="Status"
  //       style={{ width: 120 }}
  //       options={statusConfig?.map((s) => ({ label: s.label, value: s.value }))}
  //       allowClear
  //     />
  //   </Space>
  // );

  return (
    <DashboardLayout title="My Courses">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-theme-slate-800 text-3xl font-bold mb-2">
            My Courses
          </h2>
          <p className="text-theme-slate-600">
            Manage your drafts, published courses, and course performance in one
            place.
          </p>
        </div>
        <Link to="/admin/my-course/manage-course">
          <Button type="primary" size="large" className="shadow-sm">
            Create New Course
          </Button>
        </Link>
      </div>

      <CustomTable
        tableTitle="All Courses"
        columns={columns}
        dataSource={coursesData || []}
        // filterSection={filterSection}
        loading={isLoading}
        limit={limit}
        page={page}
        total={coursesData?.length || 0}
        onLimitChange={handleLimit}
        onPaginate={handlePagination}
        hideRowSelect
      />
    </DashboardLayout>
  );
}
